import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { adminService } from '../../services/adminService';
import './SimplePromotions.css';

const SimplePromotions = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    target: '',
    reward: '',
    startDate: '',
    endDate: '',
    isActive: true
  });
  const [activeTab, setActiveTab] = useState('active');

  useEffect(() => {
    loadPromotions();
  }, []);

  const loadPromotions = async () => {
    try {
      setLoading(true);
      const response = await adminService.getPromotions();
      setPromotions(response.data.promotions || []);
    } catch (error) {
      console.error('Failed to load promotions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPromotion) {
        await adminService.updatePromotion(editingPromotion._id, formData);
      } else {
        await adminService.createPromotion(formData);
      }
      
      setShowForm(false);
      setEditingPromotion(null);
      setFormData({
        title: '',
        description: '',
        target: '',
        reward: '',
        startDate: '',
        endDate: '',
        isActive: true
      });
      loadPromotions();
    } catch (error) {
      console.error('Failed to save promotion:', error);
      alert('Failed to save promotion: ' + error.message);
    }
  };

  const handleEdit = (promotion) => {
    setEditingPromotion(promotion);
    setFormData({
      title: promotion.title,
      description: promotion.description,
      target: promotion.target || '',
      reward: promotion.reward || '',
      startDate: promotion.startDate.split('T')[0],
      endDate: promotion.endDate.split('T')[0],
      isActive: promotion.isActive
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this promotion?')) {
      try {
        await adminService.deletePromotion(id);
        loadPromotions();
      } catch (error) {
        console.error('Failed to delete promotion:', error);
        alert('Failed to delete promotion: ' + error.message);
      }
    }
  };

  const getFilteredPromotions = () => {
    const now = new Date();
    return promotions.filter(promotion => {
      const endDate = new Date(promotion.endDate);
      if (activeTab === 'active') {
        return promotion.isActive && endDate >= now;
      } else {
        return promotion.isActive && endDate < now;
      }
    });
  };

  const filteredPromotions = getFilteredPromotions();

  return (
    <Layout>
      <div className="simple-promotions">
      <div className="promotions-header">
        <h1>Manage Promotions</h1>
        <button 
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
        >
          Create New Promotion
        </button>
      </div>

      {/* Tabs */}
      <div className="promotions-tabs">
        <button 
          className={`tab ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => setActiveTab('active')}
        >
          Active Promotions ({promotions.filter(p => p.isActive && new Date(p.endDate) >= new Date()).length})
        </button>
        <button 
          className={`tab ${activeTab === 'expired' ? 'active' : ''}`}
          onClick={() => setActiveTab('expired')}
        >
          Expired Promotions ({promotions.filter(p => p.isActive && new Date(p.endDate) < new Date()).length})
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingPromotion ? 'Edit Promotion' : 'Create New Promotion'}</h2>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowForm(false);
                  setEditingPromotion(null);
                  setFormData({
                    title: '',
                    description: '',
                    target: '',
                    reward: '',
                    startDate: '',
                    endDate: '',
                    isActive: true
                  });
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="promotion-form">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                  maxLength={100}
                />
              </div>
              
              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                  maxLength={500}
                  rows={4}
                />
              </div>

              <div className="form-group">
                <label>Target *</label>
                <input
                  type="text"
                  value={formData.target}
                  onChange={(e) => setFormData({...formData, target: e.target.value})}
                  required
                  maxLength={200}
                  placeholder="e.g., Install 10 inverters"
                />
              </div>

              <div className="form-group">
                <label>Reward *</label>
                <input
                  type="text"
                  value={formData.reward}
                  onChange={(e) => setFormData({...formData, reward: e.target.value})}
                  required
                  maxLength={200}
                  placeholder="e.g., PKR 50,000 bonus"
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>End Date *</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                    required
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                  />
                  Active
                </label>
              </div>
              
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingPromotion ? 'Update' : 'Create'} Promotion
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Promotions Table */}
      <div className="promotions-content">
        {loading ? (
          <div className="loading">Loading promotions...</div>
        ) : (
          <div className="promotions-table-container">
            <table className="promotions-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Target</th>
                  <th>Reward</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPromotions.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="no-data">
                      No {activeTab} promotions found
                    </td>
                  </tr>
                ) : (
                  filteredPromotions.map(promotion => (
                    <tr key={promotion._id}>
                      <td className="title-cell">{promotion.title}</td>
                      <td className="description-cell">{promotion.description}</td>
                      <td className="target-cell">{promotion.target || 'N/A'}</td>
                      <td className="reward-cell">{promotion.reward || 'N/A'}</td>
                      <td>{new Date(promotion.startDate).toLocaleDateString()}</td>
                      <td>{new Date(promotion.endDate).toLocaleDateString()}</td>
                      <td>
                        <span className={`status ${promotion.status}`}>
                          {promotion.status}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEdit(promotion)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(promotion._id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
    </Layout>
  );
};

export default SimplePromotions;
